import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Save, Plus, Trash2, Zap, Flame, Droplets, Trash,
  AlertTriangle, CheckCircle, Download, RefreshCw, Upload,
  FileText, Brain, Target, BarChart3, Info, Sparkles, ChevronDown
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';
import { useAlert } from '../../context/AlertContext';
import { formatDateTime } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

// ==================== AI OUTPUT FORMATTER ====================

// Many LLM responses come back as one giant paragraph with markdown markers
// inlined (e.g. "... text. * No-Capex Opportunities: * Optimize ..."), with
// SEU IDs jammed mid-sentence after another SEU's safety text, trailing ` -`
// bullet artifacts, stray `**`, and a Summary blob crammed into a single
// header. The parser below relies on real newlines and a clean structure, so
// inject newlines and clean up artifacts before the line-based pass.
// Idempotent — content that already has newlines passes through unchanged.
const injectNewlines = (raw) => {
  if (!raw) return raw;
  let s = raw;

  // 0. Normalise CRLF and tabs.
  s = s.replace(/\r\n/g, '\n').replace(/\t/g, ' ');

  // 1. SEU-XX-YYYYYYYY identifiers that appear mid-string — these are the
  //    start of a new per-item block. Force onto their own line and bold them.
  //    Three flavours, in order of specificity:
  //    a) SEU-ID — Equipment description: (trailing colon → end of header is clear)
  //    b) SEU-ID — Equipment description  (no colon, header runs until next bullet/period)
  //    c) bare SEU-ID:   (no equipment description on this line)
  s = s.replace(
    /(?<!\*)\s+(SEU-[A-Z]{2,3}-[A-F0-9]{6,12})(\s*[-–—:][^.\n]+?):(\s*-)?/g,
    '\n\n**$1$2:**\n'
  );
  s = s.replace(
    /(?<!\*)\s+(SEU-[A-Z]{2,3}-[A-F0-9]{6,12})(\s*[-–—][^.\n*]+?)(?=\s+(?:- |\* |1\.|Recommended|Minimum|Safety|SEU-|$|\n))/g,
    '\n\n**$1$2**\n'
  );
  s = s.replace(/(?<!\*)\s+(SEU-[A-Z]{2,3}-[A-F0-9]{6,12}):/g, '\n\n**$1:**\n');

  // 2. Markdown headers (#, ##, ###, ####) anywhere in the string → own line.
  s = s.replace(/\s+(#{1,4}\s+)/g, '\n\n$1');

  // 3. "Priority N - …" headers — these introduce a section. Promote to ###.
  s = s.replace(/(?:^|\n)?\s*(Priority\s+\d+\s*[-–—:]\s*[^\n]+?)(?=\s+SEU-[A-Z]{2,3}|\n|$)/g,
                '\n\n### $1\n');

  // 4. Known section labels followed by ':' that the LLM tends to inline.
  //    Order matters — longer labels first so substrings don't pre-match.
  const SECTION_LABELS = [
    'Likely Missing SEUs with Justification',
    "Typical Equipment that Should be Present but Isn't Listed",
    "Typical Equipment That Should Be Present But Isn't Listed",
    'Suggestions for Improving SEU Coverage',
    'No-Capex Opportunities',
    'Low-Capex Opportunities',
    'Capex Opportunities',
    'Recommended instruments',
    'Minimum measurement duration',
    'Minimum duration',
    'Safety precautions',
    'Estimated Time to Complete All Measurements',
    'Estimated time to complete all measurements',
    'Justification',
    'Summary',
    'Total SEUs identified',
    'SEUs needing measurement',
    'Overall SEU coverage',
  ];
  SECTION_LABELS.forEach((label) => {
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Match the label preceded by whitespace/punctuation (not already a newline)
    // and followed by ':'. Insert a newline before so each is its own line.
    const re = new RegExp(`(?<!\\n)[ \\t]*(\\*{0,2}${escaped}\\*{0,2}):`, 'g');
    s = s.replace(re, '\n- **$1:**');
  });

  // 5. "SEU N:" (numeric) headers inline.
  s = s.replace(/(?<!\n)\s+(SEU\s+\d+\s*[:\-—])/g, '\n\n### $1');

  // 6. Bullet markers ("* " or "- ") that appear mid-sentence after a period,
  //    comma, percent, ), or after a `:` (very common: "Pumps: - Recommended").
  s = s.replace(/(?<=[.)%,])\s+\*\s+/g, '\n* ');
  s = s.replace(/(?<=[.)%,])\s+-\s+(?=[A-Z**])/g, '\n- ');
  s = s.replace(/:\s+-\s+(?=[A-Z*])/g, ':\n- ');

  // 7. Numbered list items "1. ", "2. " mid-paragraph. Careful: don't break
  //    decimals like "2.5" — require a space *after* the digit+dot.
  s = s.replace(/(?<=[.)%])\s+(\d+)\.\s+(?=[A-Z*])/g, '\n$1. ');

  // 8. Strip stray `**` / `****` artifacts. LLM commonly emits things like
  //    "**Field:** **value**" (paired but adjacent), "**** value" (4 stars),
  //    "compressors**" (orphan trailing close), "Field:**" (close at end of
  //    label). Strategy: drop sequences of 3+ asterisks entirely, then
  //    remove obvious orphan `**` not adjacent to a balanced pair.
  s = s.replace(/\*{3,}/g, '');                       // ****  → ''
  s = s.replace(/(:|\.|,|-)\*{2}(?=\s|$)/g, '$1');    // "X:**" → "X:"
  s = s.replace(/\*{2}(?=\s+[A-Za-z0-9])/g, '');       // " **value" → " value" (orphan open)
  s = s.replace(/(?<=\S)\*{2}(?!\S)/g, '');            // "value**" → "value" (orphan close at word end)

  // 9. Strip trailing ` -` artifacts at end of label/bullet lines.
  //    Covers "... Pumps: -", "2 hours -", "27 -", "82.3% -".
  s = s.replace(/(\S)\s+-\s*(\n|$)/g, '$1$2');
  s = s.replace(/\s+\[\]\s*(\n|$)/g, '$1');

  // 10. Drop lone `###` / `####` lines that have no following heading text on
  //     the same line — the LLM emits these as separators.
  s = s.replace(/^#{1,6}\s*$/gm, '');

  // 11. Collapse 3+ consecutive newlines down to 2 (clean paragraph spacing).
  s = s.replace(/\n{3,}/g, '\n\n');

  return s.trim();
};

// Renders inline `**bold**` markdown as <strong>. Returns an array of React
// nodes so it can be embedded inside <li>/<p>/<h*>.
const renderInline = (text, keyPrefix = '') => {
  if (!text) return null;
  // Strip any leftover lone `*` that aren't part of `**bold**`.
  const cleaned = text.replace(/(^|\s)\*(?!\*)/g, '$1').replace(/(?<!\*)\*(?!\*)/g, '');
  const parts = cleaned.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={`${keyPrefix}-b-${i}`} className="font-semibold text-gray-900">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={`${keyPrefix}-t-${i}`}>{part}</span>;
  });
};

// Formats AI markdown output to clean, readable HTML
const formatAIContent = (content) => {
  if (!content) return null;

  // Pre-process: inject newlines before structural markers if the LLM
  // returned a flat one-paragraph blob.
  const prepared = injectNewlines(content);

  const lines = prepared.split('\n');
  const elements = [];
  let currentList = [];

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="space-y-1.5 mb-3 ml-1">
          {currentList.map((item, idx) => (
            <li key={idx} className="text-sm text-gray-700 flex items-start gap-2 leading-relaxed">
              <span className="text-indigo-400 mt-1.5 shrink-0 text-xs">●</span>
              <span className="flex-1">{item}</span>
            </li>
          ))}
        </ul>
      );
      currentList = [];
    }
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }

    // Strip leading "*" (not bold) which the LLM sometimes uses for bullets.
    const lineForCheck = trimmed.replace(/^\*\s+(?!\*)/, '- ');

    // Markdown heading levels — render at distinct sizes/colours so structure
    // is visible at a glance.
    const headerMatch = lineForCheck.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      flushList();
      const level = headerMatch[1].length;
      const text = headerMatch[2].replace(/\*\*/g, '').replace(/[:：]\s*$/, '');
      if (level <= 2) {
        elements.push(
          <h3 key={index} className="text-lg font-bold text-indigo-900 mt-5 mb-2 pb-1 border-b-2 border-indigo-200">
            {renderInline(text, `h2-${index}`)}
          </h3>
        );
      } else if (level === 3) {
        elements.push(
          <h4 key={index} className="text-base font-semibold text-indigo-800 mt-4 mb-2">
            {renderInline(text, `h3-${index}`)}
          </h4>
        );
      } else {
        elements.push(
          <h5 key={index} className="text-sm font-semibold text-gray-700 mt-3 mb-1.5 uppercase tracking-wide">
            {renderInline(text, `h4-${index}`)}
          </h5>
        );
      }
      return;
    }

    // Numbered top-level items "1. ", "2. " — treat as a small heading
    if (/^(\*\*)?\d+\.\s/.test(lineForCheck)) {
      flushList();
      const text = lineForCheck.replace(/^\*\*?/, '').replace(/\*\*?$/, '');
      elements.push(
        <h5 key={index} className="text-sm font-semibold text-gray-800 mt-3 mb-1">
          {renderInline(text, `n-${index}`)}
        </h5>
      );
      return;
    }

    // Short label-style sub-headers ("**No-Capex Opportunities:**", "Justification:")
    const labelCheck = trimmed.replace(/^\*+|\*+$/g, '').replace(/\*\*/g, '').trim();
    if ((labelCheck.endsWith(':') || /^SEU-[A-Z]{2,3}-[A-F0-9]{6,12}/.test(labelCheck))
         && labelCheck.length < 80 && !labelCheck.includes('. ') && !lineForCheck.startsWith('-')) {
      flushList();
      const text = labelCheck.endsWith(':') ? labelCheck.slice(0, -1) : labelCheck;
      // SEU-ID rows get a prominent card-like heading; generic colon-labels
      // get a more compact gray sub-header.
      const isSeuHeading = /^SEU-[A-Z]{2,3}-[A-F0-9]{6,12}/.test(text);
      if (isSeuHeading) {
        elements.push(
          <div key={index} className="mt-5 mb-3 px-3 py-2 bg-indigo-50 border-l-4 border-indigo-400 rounded">
            <div className="text-sm font-bold text-indigo-900 tracking-tight">
              {renderInline(text, `seu-${index}`)}
            </div>
          </div>
        );
      } else {
        elements.push(
          <h5 key={index} className="text-sm font-semibold text-gray-700 mt-3 mb-1.5">
            {renderInline(text, `lbl-${index}`)}
          </h5>
        );
      }
      return;
    }

    // List items
    if (lineForCheck.startsWith('-')) {
      const text = lineForCheck.replace(/^-\s*/, '');
      // Key: value rendering — key in bold, value normal
      const keyValMatch = text.match(/^\*\*([^*]+)\*\*:?\s*(.*)$/);
      if (keyValMatch) {
        const [, key, value] = keyValMatch;
        currentList.push(
          <>
            <strong className="font-semibold text-gray-900">{key.trim()}:</strong>{' '}
            <span>{renderInline(value, `kv-${index}`)}</span>
          </>
        );
        return;
      }
      if (text.includes(':')) {
        const colonIndex = text.indexOf(':');
        const key = text.substring(0, colonIndex).trim().replace(/\*\*/g, '');
        const value = text.substring(colonIndex + 1).trim();
        if (key.length < 40 && value) {
          currentList.push(
            <>
              <strong className="font-semibold text-gray-900">{key}:</strong>{' '}
              <span>{renderInline(value, `kv2-${index}`)}</span>
            </>
          );
          return;
        }
      }
      currentList.push(<span>{renderInline(text, `li-${index}`)}</span>);
      return;
    }

    // Regular paragraph
    flushList();
    elements.push(
      <p key={index} className="text-sm text-gray-700 mb-3 leading-relaxed">
        {renderInline(trimmed, `p-${index}`)}
      </p>
    );
  });

  flushList();
  return elements;
};

// ==================== REFERENCE DATA ====================

const EQUIPMENT_TYPES = {
  electricity: {
    systems: [
      'Compressed Air System', 'Chilled Water System', 'Cooling Water System',
      'HVAC System', 'Process Motor System', 'Lighting System',
      'Material Handling System', 'Production Line System',
      // Foundry / fabrication / machining additions
      'Melting System', 'Heat Treatment System', 'Machining System',
      'Fabrication System', 'Finishing System', 'Utilities System',
      'ETP / STP System', 'Steam System', 'Refrigeration System',
      // Distillery / process-industry additions
      'Distillation System', 'Fermentation System', 'Drying System',
      // Foundry casting-line extended taxonomy (matches values produced by
      // the Machine_List → SEU template generator). Kept as short labels
      // (no " System" suffix) to match how operators typically refer to
      // these on the shop floor.
      'Moulding', 'Sand Handling', 'Core Making', 'Material Handling',
      'Finishing', 'Compressed Air', 'Utilities', 'Heat Treatment',
      'Melting', 'Quality / Lab',
      // EV bus / commercial-vehicle assembly additions. Both labels are used
      // by the client in the System AND Group column, so they appear in both
      // lists below.
      'Vehicle Testing', 'EV Charger',
      'Other'
    ],
    groups: [
      'Compressors', 'Chillers', 'Pumps', 'Motors', 'Fans', 'Cooling Towers',
      'AHUs', 'Transformers', 'Lighting', 'Conveyors',
      // Foundry / fabrication / machining additions
      'Induction Furnaces', 'Furnace Auxiliaries', 'Heat Treatment Furnaces',
      'CNC Machines', 'VMC Machines', 'Grinders', 'Welding Rectifiers',
      'Material Handling Equipment', 'Shot Blasting Machines', 'Paint Booths',
      'ETP Blowers', 'HVAC', 'Process Pumps', 'Cooling Water Pumps',
      // Distillery / process-industry additions
      'Agitators', 'Centrifuges', 'Aeration Blowers',
      // Foundry casting-line extended taxonomy
      'Moulding Machines', 'Moulding Hoists', 'Moulding Line Ancillary',
      'Belt Conveyors', 'Bucket Elevators', 'Screw Conveyors', 'Vibratory Feeders',
      'EOT Cranes', 'Pouring Cranes', 'Hoists / Jib Cranes', 'Specialty Cranes',
      'Transfer Trolleys', 'Sand Mixers', 'Sand Coolers', 'Sand Sievers',
      'Knockout / Shakeout', 'Dust Collection', 'Skip Chargers',
      'Cold Box Machines', 'Core Sand Mixers', 'Core Ovens', 'Core Shop Equipment',
      'Ladles', 'Bench Grinders', 'Cutting Saws', 'Air Dryers', 'Generators',
      'Testing Equipment',
      // EV bus / commercial-vehicle assembly additions (see systems above)
      'Vehicle Testing', 'EV Charger',
      'Miscellaneous',
      'Other'
    ]
  },
  fuel: [
    'Boilers', 'Furnaces', 'Ovens', 'Kilns', 'Thermic Fluid Heaters',
    'DG Sets', 'Hot Air Generators', 'Dryers', 'Incinerators',
    // Foundry / fabrication / machining additions
    'Induction Furnaces', 'Heat Treatment Furnaces', 'Ladle Heaters',
    'Preheat Burners', 'Cutting Torches', 'Brazing Equipment',
    'Canteen / Kitchen', 'Mobile Equipment (Forklifts / Vehicles)',
    'Other'
  ],
  water: [
    'Cooling Towers (Make-up)', 'Cooling Towers (Blowdown)', 'RO Plants',
    'DM Plants', 'Process Washing', 'Domestic High-Use', 'ETP', 'STP',
    // Foundry / fabrication / machining additions
    'Scrubbers / Air Pollution Control', 'Quench Tanks',
    'Gardening / Horticulture', 'Floor Washing / Housekeeping',
    'Fire System', 'Drinking Water (RO)',
    // Distillery / process-industry additions
    'Process Water (Mash/Dilution)', 'Boiler Feed Make-up', 'CIP Systems',
    'Other'
  ],
  waste: [
    'ETP/MEE Sludge', 'High-Volume Scrap', 'Hazardous Waste',
    'Packaging Waste', 'Metal Scrap', 'Plastic Waste', 'E-Waste',
    // Foundry / fabrication / machining additions
    'Used / Spent Oil', 'Paint Sludge', 'Oil-Soaked Cotton / Rags',
    'Used Oil Filters', 'Foundry Sand (Spent)', 'Wood / Packaging Waste',
    'Canteen / Food Waste',
    // Distillery / process-industry additions
    'Spent Wash / Vinasse', 'DDGS (Distillers Grains)', 'Boiler Ash',
    'Filter Press Cake',
    'Other'
  ]
};

const SEU_LEVELS = ['System', 'Group', 'Equipment'];

const CONTROL_TYPES = ['On/Off', 'VFD', 'Throttling', 'Load/Unload', 'Direct Online', 'Star Delta', 'Modulating', 'Manual'];

const CONTROL_FACTORS = {
  'On/Off': 1.00, 'VFD': 0.90, 'Throttling': 1.10, 'Load/Unload': 1.05,
  'Direct Online': 1.00, 'Star Delta': 1.00, 'Modulating': 0.95, 'Manual': 1.05
};

const FUEL_TYPES = ['PNG', 'Diesel', 'FO', 'LPG', 'Coal', 'Biomass', 'HSD', 'Bagasse', 'Spent Wash'];
// NCV in GJ/kg — Bagasse ~10.2 GJ/T (50% moisture), Spent Wash ~13 GJ/T (60% solids, ZLD concentrate)
const FUEL_NCV = { 'PNG': 0.0358, 'Diesel': 0.0358, 'FO': 0.0411, 'LPG': 0.0473, 'Coal': 0.0209, 'Biomass': 0.0138, 'HSD': 0.0358, 'Bagasse': 0.0102, 'Spent Wash': 0.0130 };

const WATER_SOURCES = ['Municipal', 'Borewell', 'Tanker', 'River', 'Recycled'];
const WASTE_CATEGORIES = [
  'Hazardous', 'Non-Hazardous', 'E-Waste', 'Recyclable',
  // Added for fabrication / foundry / machining streams
  'Metal Scrap', 'Plastic', 'Organic / Food', 'Biomedical'
];
const DISPOSAL_METHODS = [
  'Incineration', 'Landfill', 'Recycling', 'Recovery', 'Treatment',
  // Added to cover Indian HW Rules authorized streams
  'Authorized TSDF', 'Authorized Recycler', 'Sold to Vendor',
  'Composting', 'Co-Processing', 'Reused',
  // Distillery / biomass-industry addition (vinasse → biogas digester)
  'Bio-methanation'
];

// ==================== CALCULATION FUNCTIONS ====================

const calculateElectricalEnergy = (row) => {
  if (!row.rated_kw || !row.load_percent || !row.hours_per_day) return null;
  const controlFactor = CONTROL_FACTORS[row.control_type] || 1.0;
  const qty = row.quantity || 1;
  return Math.round(row.rated_kw * qty * (row.load_percent / 100) * row.hours_per_day * 365 * controlFactor);
};

const calculateFuelEnergy = (row) => {
  if (!row.monthly_fuel_qty || !row.fuel_type) return null;
  const ncv = FUEL_NCV[row.fuel_type] || 0.035;
  return Math.round(row.monthly_fuel_qty * 12 * ncv * 100) / 100;
};

const calculateWaterAnnual = (row) => {
  if (!row.daily_kl) return null;
  return Math.round(row.daily_kl * 365);
};

const calculateWasteAnnual = (row) => {
  if (!row.monthly_qty) return null;
  return Math.round(row.monthly_qty * 12 * 100) / 100;
};

const determineConfidence = (row, resourceType) => {
  if (resourceType === 'electricity') {
    if (row.seu_level === 'System' || row.seu_level === 'Group') return 'Low';
    if (row.rated_kw && row.load_percent && row.hours_per_day) return 'Medium';
    return 'Low';
  }
  if (resourceType === 'fuel') {
    return row.monthly_fuel_qty ? 'Medium' : 'Low';
  }
  if (resourceType === 'water') {
    return row.daily_kl ? 'Medium' : 'Low';
  }
  if (resourceType === 'waste') {
    return row.monthly_qty ? 'Medium' : 'Low';
  }
  return 'Low';
};

// ==================== CSV FUNCTIONS ====================

const parseCSV = (text) => {
  const lines = text.split('\n').filter(line => line.trim());
  if (lines.length < 2) return [];
  
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
  const rows = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim());
    const row = {};
    headers.forEach((header, idx) => {
      row[header] = values[idx] || '';
    });
    rows.push(row);
  }
  return rows;
};

const generateCSVTemplate = (resourceType) => {
  const templates = {
    electricity: 'level,system,group,equipment,rated_kw,quantity,load_percent,hours_per_day,control_type,remarks\nEquipment,Compressed Air,Compressors,CMP-01,160,1,85,22,Load/Unload,Main compressor',
    fuel: 'equipment,location,fuel_type,monthly_fuel_qty,fuel_unit,load_percent,hours_per_day,remarks\nBoilers,Utilities,PNG,50000,SCM,80,20,Steam boiler',
    water: 'equipment,location,source,daily_kl,recycling,remarks\nCooling Towers (Make-up),Utilities,Borewell,50,No,CT makeup',
    waste: 'equipment,location,waste_type,category,monthly_qty,unit,disposal_method,remarks\nETP/MEE Sludge,ETP,Hazardous,Cat 34.1,5,MT,Incineration,ETP sludge'
  };
  return templates[resourceType] || '';
};

// ==================== MAIN COMPONENT ====================

function SEUModule({ studyIdProp, embedded = false }) {
  const { showSuccess, showError, showWarning, showInfo } = useAlert();
  const params = useParams();
  const studyId = studyIdProp || params.studyId;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // State
  const [activeTab, setActiveTab] = useState('seu-tables');
  const [activeResourceTab, setActiveResourceTab] = useState('electricity');
  const [study, setStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // SEU Data - local state for editing
  const [electricalData, setElectricalData] = useState([]);
  const [fuelData, setFuelData] = useState([]);
  const [waterData, setWaterData] = useState([]);
  const [wasteData, setWasteData] = useState([]);
  
  // Summary data
  const [seuSummary, setSeuSummary] = useState(null);
  
  // AI Analysis
  const [aiLoading, setAiLoading] = useState(false);
  const [expandedAnalysis, setExpandedAnalysis] = useState({
    missing_seus: true,
    measurement_strategy: true,
    opportunity_seeding: true
  });
  
  // Create empty row functions
  const createEmptyElectricalRow = () => ({
    id: `new_${Date.now()}_${Math.random()}`,
    seu_level: 'Equipment',
    system_name: '',
    group_name: '',
    equipment_type: '',
    rated_kw: '',
    quantity: 1,
    load_percent: '',
    hours_per_day: '',
    control_type: 'On/Off',
    annual_kwh: null,
    confidence: 'Low',
    is_seu: false,
    remarks: ''
  });
  
  const createEmptyFuelRow = () => ({
    id: `new_${Date.now()}_${Math.random()}`,
    equipment_type: '',
    process_area: '',
    fuel_type: 'PNG',
    monthly_fuel_qty: '',
    fuel_unit: 'SCM',
    load_percent: '',
    hours_per_day: '',
    annual_gj: null,
    confidence: 'Low',
    is_seu: false,
    remarks: ''
  });
  
  const createEmptyWaterRow = () => ({
    id: `new_${Date.now()}_${Math.random()}`,
    equipment_type: '',
    process_area: '',
    source: 'Municipal',
    daily_kl: '',
    annual_kl: null,
    recycling: false,
    confidence: 'Low',
    is_seu: false,
    remarks: ''
  });
  
  const createEmptyWasteRow = () => ({
    id: `new_${Date.now()}_${Math.random()}`,
    equipment_type: '',
    process_area: '',
    waste_type: 'Non-Hazardous',
    category: '',
    monthly_qty: '',
    unit: 'MT',
    annual_qty: null,
    disposal_method: '',
    confidence: 'Low',
    is_seu: false,
    remarks: ''
  });
  
  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const studyRes = await axios.get(`${API}/api/ers/studies/${studyId}`, config);
      setStudy(studyRes.data);
      
      // Fetch existing SEU data
      const [elecRes, fuelRes, waterRes, wasteRes, summaryRes] = await Promise.all([
        axios.get(`${API}/api/ers/seu/${studyId}/electricity`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/api/ers/seu/${studyId}/fuel`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/api/ers/seu/${studyId}/water`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/api/ers/seu/${studyId}/waste`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/api/ers/seu/summary/${studyId}`, config).catch(() => ({ data: null }))
      ]);
      
      // Transform data for local editing
      setElectricalData(elecRes.data?.length > 0 ? elecRes.data.map(row => ({
        ...row,
        rated_kw: row.rated_capacity || '',
        annual_kwh: row.final_annual_energy || null,
        confidence: row.confidence_level || 'Low'
      })) : [createEmptyElectricalRow()]);
      
      setFuelData(fuelRes.data?.length > 0 ? fuelRes.data.map(row => ({
        ...row,
        annual_gj: row.fuel_energy_gj_yr || null,
        confidence: row.confidence_level || 'Low'
      })) : [createEmptyFuelRow()]);
      
      setWaterData(waterRes.data?.length > 0 ? waterRes.data.map(row => ({
        ...row,
        daily_kl: row.avg_daily_consumption_kl || '',
        annual_kl: row.annual_kl || null,
        recycling: row.recycling_in_place || false,
        confidence: row.confidence_level || 'Low'
      })) : [createEmptyWaterRow()]);
      
      setWasteData(wasteRes.data?.length > 0 ? wasteRes.data.map(row => ({
        ...row,
        monthly_qty: row.avg_monthly_qty || '',
        annual_qty: row.annual_qty || null,
        confidence: row.confidence_level || 'Low'
      })) : [createEmptyWasteRow()]);
      
      setSeuSummary(summaryRes.data);
      
    } catch (error) {
      console.error('Error fetching SEU data:', error);
    } finally {
      setLoading(false);
    }
  }, [studyId]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Save data to backend
  const saveData = async (resourceType) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      let dataToSave = [];
      let endpoint = '';
      
      if (resourceType === 'electricity') {
        dataToSave = electricalData.filter(row => row.equipment_type).map(row => ({
          study_id: studyId,
          resource_type: 'Electricity',
          seu_level: row.seu_level,
          system_name: row.system_name,
          group_name: row.group_name,
          equipment_type: row.equipment_type,
          rated_capacity: parseFloat(row.rated_kw) || null,
          quantity: parseInt(row.quantity) || 1,
          load_percent: parseFloat(row.load_percent) || null,
          hours_per_day: parseFloat(row.hours_per_day) || null,
          control_type: row.control_type,
          remarks: row.remarks,
          is_seu_override: row.is_seu
        }));
        endpoint = 'electricity';
      } else if (resourceType === 'fuel') {
        dataToSave = fuelData.filter(row => row.equipment_type).map(row => ({
          study_id: studyId,
          resource_type: 'Fuel',
          equipment_type: row.equipment_type,
          process_area: row.process_area,
          fuel_type: row.fuel_type,
          monthly_fuel_qty: parseFloat(row.monthly_fuel_qty) || null,
          fuel_unit: row.fuel_unit,
          load_percent: parseFloat(row.load_percent) || null,
          hours_per_day: parseFloat(row.hours_per_day) || null,
          remarks: row.remarks,
          is_seu_override: row.is_seu
        }));
        endpoint = 'fuel';
      } else if (resourceType === 'water') {
        dataToSave = waterData.filter(row => row.equipment_type).map(row => ({
          study_id: studyId,
          resource_type: 'Water',
          equipment_type: row.equipment_type,
          process_area: row.process_area,
          source: row.source,
          avg_daily_consumption_kl: parseFloat(row.daily_kl) || null,
          recycling_in_place: row.recycling,
          remarks: row.remarks,
          is_seu_override: row.is_seu
        }));
        endpoint = 'water';
      } else if (resourceType === 'waste') {
        dataToSave = wasteData.filter(row => row.equipment_type).map(row => ({
          study_id: studyId,
          resource_type: 'Waste',
          equipment_type: row.equipment_type,
          process_area: row.process_area,
          waste_type: row.waste_type,
          category: row.category,
          avg_monthly_qty: parseFloat(row.monthly_qty) || null,
          unit: row.unit,
          disposal_method: row.disposal_method,
          remarks: row.remarks,
          is_seu_override: row.is_seu
        }));
        endpoint = 'waste';
      }
      
      // Use bulk save endpoint
      await axios.post(`${API}/api/ers/seu/bulk/${studyId}/${endpoint}`, dataToSave, config);

      showSuccess(`${resourceType} data saved successfully!`);
      setHasChanges(false);
      await fetchData(); // Refresh to get updated calculations
    } catch (error) {
      console.error('Error saving data:', error);
      showError(`Failed to save ${resourceType} data. Please try again.`);
    } finally {
      setSaving(false);
    }
  };
  
  // CSV Import
  // Merge imported rows into whatever is already on screen, keyed on equipment
  // name: same name is replaced, new name is added.
  //
  // This used to be a plain append (`[...prev.filter(...), ...newRows]`), so
  // re-importing a corrected version of the same file kept BOTH copies. One
  // KFIL study reached 74 live rows for 37 pieces of equipment, double-counting
  // every SEU and reporting twice the site's metered energy. Importing a file
  // that introduces genuinely new equipment still works as before.
  const mergeImportedRows = (prev, newRows) => {
    const key = (r) => (r.equipment_type || '').trim().toLowerCase();
    const incoming = new Set(newRows.map(key).filter(Boolean));
    const kept = prev.filter((r) => r.equipment_type && !incoming.has(key(r)));
    return [...kept, ...newRows];
  };

  const handleCsvImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const rows = parseCSV(e.target.result);
      
      if (activeResourceTab === 'electricity') {
        const newRows = rows.map(row => ({
          ...createEmptyElectricalRow(),
          seu_level: row.level || 'Equipment',
          system_name: row.system || '',
          group_name: row.group || '',
          equipment_type: row.equipment || '',
          rated_kw: row.rated_kw || '',
          quantity: parseInt(row.quantity) || 1,
          load_percent: row.load_percent || '',
          hours_per_day: row.hours_per_day || '',
          control_type: row.control_type || 'On/Off',
          remarks: row.remarks || ''
        }));
        setElectricalData(prev => mergeImportedRows(prev, newRows));
      } else if (activeResourceTab === 'fuel') {
        const newRows = rows.map(row => ({
          ...createEmptyFuelRow(),
          equipment_type: row.equipment || '',
          process_area: row.location || '',
          fuel_type: row.fuel_type || 'PNG',
          monthly_fuel_qty: row.monthly_fuel_qty || '',
          fuel_unit: row.fuel_unit || 'SCM',
          remarks: row.remarks || ''
        }));
        setFuelData(prev => mergeImportedRows(prev, newRows));
      } else if (activeResourceTab === 'water') {
        const newRows = rows.map(row => ({
          ...createEmptyWaterRow(),
          equipment_type: row.equipment || '',
          process_area: row.location || '',
          source: row.source || 'Municipal',
          daily_kl: row.daily_kl || '',
          recycling: row.recycling === 'Yes',
          remarks: row.remarks || ''
        }));
        setWaterData(prev => mergeImportedRows(prev, newRows));
      } else if (activeResourceTab === 'waste') {
        const newRows = rows.map(row => ({
          ...createEmptyWasteRow(),
          equipment_type: row.equipment || '',
          process_area: row.location || '',
          waste_type: row.waste_type || 'Non-Hazardous',
          category: row.category || '',
          monthly_qty: row.monthly_qty || '',
          unit: row.unit || 'MT',
          disposal_method: row.disposal_method || '',
          remarks: row.remarks || ''
        }));
        setWasteData(prev => mergeImportedRows(prev, newRows));
      }

      setHasChanges(true);
      showInfo(`Imported ${rows.length} rows — existing rows with the same equipment name were replaced. Remember to click Save!`);
    };
    reader.readAsText(file);
    event.target.value = '';
  };
  
  // Download template
  const downloadTemplate = () => {
    const csv = generateCSVTemplate(activeResourceTab);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `SEU_${activeResourceTab}_template.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  
  // AI Analysis - Generate all at once
  const [aiAnalyses, setAiAnalyses] = useState(null);
  
  const generateAllAiAnalyses = async () => {
    setAiLoading(true);
    setAiAnalyses(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/ers/seu/ai-analysis-all/${studyId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiAnalyses(res.data);
    } catch (error) {
      console.error('AI Analysis error:', error);
      setAiAnalyses({ error: 'Failed to generate AI analyses. Please try again.' });
    } finally {
      setAiLoading(false);
    }
  };
  
  // Resource tabs
  const resourceTabs = [
    { id: 'electricity', label: 'Electrical SEUs', icon: Zap, color: 'yellow' },
    { id: 'fuel', label: 'Thermal/Fuel SEUs', icon: Flame, color: 'orange' },
    { id: 'water', label: 'Water SEUs', icon: Droplets, color: 'blue' },
    { id: 'waste', label: 'Waste SEUs', icon: Trash, color: 'green' }
  ];
  
  if (loading) {
    return (
      <div className={`${embedded ? '' : 'min-h-screen'} bg-gray-50 flex items-center justify-center py-8`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? '' : 'min-h-screen'} bg-gray-50`}>
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            {!embedded && (
              <button onClick={() => navigate(`/ers/study/${studyId}`)} className="flex items-center text-blue-600 hover:text-blue-800 mb-2 text-sm">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to Study
              </button>
            )}
            <h1 className="text-2xl font-bold text-gray-900">SEU Identification & Prioritisation</h1>
            <p className="text-gray-600 text-sm">{study?.study_name} - Desktop Phase</p>
          </div>
          {hasChanges && (
            <div className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm">Unsaved changes</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Main Tabs */}
      <div className="bg-white border-b">
        <div className="flex px-6">
          <button
            onClick={() => setActiveTab('seu-tables')}
            className={`px-4 py-3 font-medium text-sm border-b-2 ${activeTab === 'seu-tables' ? 'text-blue-600 border-blue-600' : 'text-gray-600 border-transparent'}`}
          >
            <Target className="w-4 h-4 inline mr-2" /> SEU Tables
          </button>
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-3 font-medium text-sm border-b-2 ${activeTab === 'dashboard' ? 'text-blue-600 border-blue-600' : 'text-gray-600 border-transparent'}`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" /> Dashboard
          </button>
          <button
            onClick={() => setActiveTab('ai-analysis')}
            className={`px-4 py-3 font-medium text-sm border-b-2 ${activeTab === 'ai-analysis' ? 'text-blue-600 border-blue-600' : 'text-gray-600 border-transparent'}`}
          >
            <Brain className="w-4 h-4 inline mr-2" /> AI Analysis
          </button>
        </div>
      </div>
      
      <div className="p-6">
        {/* SEU TABLES TAB */}
        {activeTab === 'seu-tables' && (
          <div className="bg-white rounded-xl shadow-sm border">
            {/* Resource Tabs */}
            <div className="flex border-b">
              {resourceTabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveResourceTab(tab.id)}
                    className={`flex items-center gap-2 px-5 py-3 font-medium text-sm border-b-2 ${
                      activeResourceTab === tab.id
                        ? `text-${tab.color}-600 border-${tab.color}-500`
                        : 'text-gray-600 border-transparent'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
            
            {/* Action Bar */}
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center flex-wrap gap-3">
              <div className="text-sm text-gray-600">
                <Info className="w-4 h-4 inline mr-1" />
                Enter data directly in the table. Derived values update automatically.
              </div>
              <div className="flex gap-2">
                <button onClick={downloadTemplate} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 flex items-center gap-1">
                  <Download className="w-4 h-4" /> Template
                </button>
                <input type="file" ref={fileInputRef} onChange={handleCsvImport} accept=".csv" className="hidden" />
                <button onClick={() => fileInputRef.current?.click()} className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 flex items-center gap-1">
                  <Upload className="w-4 h-4" /> Import CSV
                </button>
                <button
                  onClick={() => saveData(activeResourceTab)}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center gap-2 text-sm disabled:opacity-50"
                >
                  <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Data'}
                </button>
              </div>
            </div>
            
            {/* ELECTRICAL TABLE */}
            {activeResourceTab === 'electricity' && (
              <ElectricalTable 
                data={electricalData} 
                setData={(d) => { setElectricalData(d); setHasChanges(true); }}
                createEmptyRow={createEmptyElectricalRow}
              />
            )}
            
            {/* FUEL TABLE */}
            {activeResourceTab === 'fuel' && (
              <FuelTable 
                data={fuelData} 
                setData={(d) => { setFuelData(d); setHasChanges(true); }}
                createEmptyRow={createEmptyFuelRow}
              />
            )}
            
            {/* WATER TABLE */}
            {activeResourceTab === 'water' && (
              <WaterTable 
                data={waterData} 
                setData={(d) => { setWaterData(d); setHasChanges(true); }}
                createEmptyRow={createEmptyWaterRow}
              />
            )}
            
            {/* WASTE TABLE */}
            {activeResourceTab === 'waste' && (
              <WasteTable 
                data={wasteData} 
                setData={(d) => { setWasteData(d); setHasChanges(true); }}
                createEmptyRow={createEmptyWasteRow}
              />
            )}
          </div>
        )}
        
        {/* DASHBOARD TAB */}
        {activeTab === 'dashboard' && seuSummary && (
          <div className="space-y-6">
            {!seuSummary.meets_80_percent_threshold && (
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <div>
                  <h3 className="font-medium text-amber-800">SEU Coverage Below 80%</h3>
                  <p className="text-sm text-amber-700">Current: {seuSummary.seu_coverage_percent?.overall_energy?.toFixed(1)}%</p>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <h3 className="text-sm font-medium text-gray-500">Total SEUs</h3>
                <p className="text-3xl font-bold mt-2">{seuSummary.seu_counts?.total || 0}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <h3 className="text-sm font-medium text-gray-500">Energy Coverage</h3>
                <p className="text-3xl font-bold mt-2">{seuSummary.seu_coverage_percent?.overall_energy?.toFixed(1) || 0}%</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <h3 className="text-sm font-medium text-gray-500">Electrical SEU kWh</h3>
                <p className="text-2xl font-bold mt-2">{(seuSummary.total_seu_energy_by_category?.electrical_kwh || 0).toLocaleString()}</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm border p-5">
                <h3 className="text-sm font-medium text-gray-500">Fuel SEU GJ</h3>
                <p className="text-2xl font-bold mt-2">{(seuSummary.total_seu_energy_by_category?.fuel_gj || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* AI ANALYSIS TAB */}
        {activeTab === 'ai-analysis' && (
          <div className="space-y-6">
            {/* Header and Generate Button */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Brain className="w-6 h-6 text-purple-600" />
                    AI-Powered SEU Analysis
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Generate comprehensive AI insights for your SEU inventory including missing equipment detection, 
                    measurement planning, and energy-saving opportunities.
                  </p>
                </div>
                <button 
                  onClick={generateAllAiAnalyses} 
                  disabled={aiLoading}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 flex items-center gap-2 shadow-lg"
                >
                  {aiLoading ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Generate All Analyses
                    </>
                  )}
                </button>
              </div>
              
              {/* SEU Summary Cards */}
              {aiAnalyses?.seu_summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 pt-4 border-t">
                  <div className="bg-yellow-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-700">{aiAnalyses.seu_summary.electrical_count}</p>
                    <p className="text-xs text-yellow-600">Electrical SEUs</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-orange-700">{aiAnalyses.seu_summary.fuel_count}</p>
                    <p className="text-xs text-orange-600">Fuel SEUs</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-700">{aiAnalyses.seu_summary.water_count}</p>
                    <p className="text-xs text-blue-600">Water SEUs</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">{aiAnalyses.seu_summary.waste_count}</p>
                    <p className="text-xs text-green-600">Waste SEUs</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-700">{aiAnalyses.seu_summary.coverage?.toFixed(1)}%</p>
                    <p className="text-xs text-purple-600">SEU Coverage</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Error State */}
            {aiAnalyses?.error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <p className="text-red-700">{aiAnalyses.error}</p>
              </div>
            )}
            
            {/* Loading State */}
            {aiLoading && (
              <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-600 font-medium">Generating AI analyses...</p>
                <p className="text-sm text-gray-500 mt-1">This may take 15-30 seconds for comprehensive insights.</p>
              </div>
            )}
            
            {/* Analysis Results */}
            {aiAnalyses?.analyses && !aiLoading && (
              <div className="space-y-4">
                {/* Missing SEUs Analysis */}
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <button 
                    onClick={() => setExpandedAnalysis(prev => ({ ...prev, missing_seus: !prev.missing_seus }))}
                    className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🔍</span>
                      <div className="text-left">
                        <h4 className="font-semibold text-gray-900">{aiAnalyses.analyses.missing_seus.title}</h4>
                        <p className="text-sm text-gray-600">{aiAnalyses.analyses.missing_seus.description}</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${expandedAnalysis.missing_seus ? 'rotate-180' : ''}`} />
                  </button>
                  {expandedAnalysis.missing_seus && (
                    <div className="px-6 py-5 bg-white border-t">
                      <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 px-5 py-4 rounded-lg shadow-inner">
                        {formatAIContent(aiAnalyses.analyses.missing_seus.content)}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Measurement Strategy Analysis */}
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <button 
                    onClick={() => setExpandedAnalysis(prev => ({ ...prev, measurement_strategy: !prev.measurement_strategy }))}
                    className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">📊</span>
                      <div className="text-left">
                        <h4 className="font-semibold text-gray-900">{aiAnalyses.analyses.measurement_strategy.title}</h4>
                        <p className="text-sm text-gray-600">{aiAnalyses.analyses.measurement_strategy.description}</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${expandedAnalysis.measurement_strategy ? 'rotate-180' : ''}`} />
                  </button>
                  {expandedAnalysis.measurement_strategy && (
                    <div className="px-6 py-5 bg-white border-t">
                      <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 px-5 py-4 rounded-lg shadow-inner">
                        {formatAIContent(aiAnalyses.analyses.measurement_strategy.content)}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Opportunity Seeding Analysis */}
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <button 
                    onClick={() => setExpandedAnalysis(prev => ({ ...prev, opportunity_seeding: !prev.opportunity_seeding }))}
                    className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">💡</span>
                      <div className="text-left">
                        <h4 className="font-semibold text-gray-900">{aiAnalyses.analyses.opportunity_seeding.title}</h4>
                        <p className="text-sm text-gray-600">{aiAnalyses.analyses.opportunity_seeding.description}</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${expandedAnalysis.opportunity_seeding ? 'rotate-180' : ''}`} />
                  </button>
                  {expandedAnalysis.opportunity_seeding && (
                    <div className="px-6 py-5 bg-white border-t">
                      <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 px-5 py-4 rounded-lg shadow-inner">
                        {formatAIContent(aiAnalyses.analyses.opportunity_seeding.content)}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Generated At */}
                <p className="text-xs text-gray-500 text-right">
                  Generated at: {formatDateTime(aiAnalyses.generated_at)}
                </p>
              </div>
            )}
            
            {/* Empty State */}
            {!aiAnalyses && !aiLoading && (
              <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
                <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-700 mb-2">No analyses generated yet</h4>
                <p className="text-sm text-gray-500 mb-4">
                  Click "Generate All Analyses" to get AI-powered insights for your SEU inventory.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left max-w-3xl mx-auto">
                  <div className="bg-amber-50 rounded-lg p-4">
                    <h5 className="font-medium text-amber-800 flex items-center gap-2">
                      <span>🔍</span> Missing SEUs
                    </h5>
                    <p className="text-xs text-amber-700 mt-1">Identifies equipment likely missing from your inventory based on industry patterns.</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h5 className="font-medium text-blue-800 flex items-center gap-2">
                      <span>📊</span> Measurement Strategy
                    </h5>
                    <p className="text-xs text-blue-700 mt-1">Prioritized on-site measurement plan with instruments and safety notes.</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h5 className="font-medium text-green-800 flex items-center gap-2">
                      <span>💡</span> Opportunities
                    </h5>
                    <p className="text-xs text-green-700 mt-1">Energy-saving opportunities categorized by investment level.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== ELECTRICAL TABLE ====================
const ElectricalTable = ({ data, setData, createEmptyRow }) => {
  const updateRow = (index, field, value) => {
    const newData = [...data];
    newData[index] = { ...newData[index], [field]: value };
    
    // Recalculate derived values
    const row = newData[index];
    row.annual_kwh = calculateElectricalEnergy(row);
    row.confidence = determineConfidence(row, 'electricity');
    
    setData(newData);
  };
  
  const addRow = () => setData([...data, createEmptyRow()]);
  const removeRow = (index) => setData(data.filter((_, i) => i !== index));
  
  // Calculate totals and SEU status
  const totalKwh = data.reduce((sum, row) => sum + (row.annual_kwh || 0), 0);
  const dataWithShare = data.map(row => ({
    ...row,
    share_percent: totalKwh > 0 ? ((row.annual_kwh || 0) / totalKwh * 100) : 0,
    auto_seu: totalKwh > 0 ? ((row.annual_kwh || 0) / totalKwh * 100) >= 5 : false
  }));
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1960px] table-fixed text-sm">
        {/* Widths are set here, not left to the browser: equipment names run to
            ~265px ("D50 INDUCTION FURNACE 750 ...") and were being clipped
            while short select columns kept surplus width. */}
        <colgroup>
          <col style={{ width: 120 }} />{/* Level */}
          <col style={{ width: 190 }} />{/* System */}
          <col style={{ width: 210 }} />{/* Group */}
          <col style={{ width: 360 }} />{/* Equipment — longest free text */}
          <col style={{ width: 85 }} />{/* kW */}
          <col style={{ width: 65 }} />{/* Qty */}
          <col style={{ width: 80 }} />{/* Load% */}
          <col style={{ width: 85 }} />{/* Hrs/Day */}
          <col style={{ width: 130 }} />{/* Control */}
          <col style={{ width: 120 }} />{/* Est kWh/yr */}
          <col style={{ width: 75 }} />{/* Share% */}
          <col style={{ width: 120 }} />{/* Conf */}
          <col style={{ width: 50 }} />{/* SEU */}
          <col style={{ width: 200 }} />{/* Remarks */}
          <col style={{ width: 50 }} />{/* row actions */}
        </colgroup>
        <thead className="bg-yellow-50">
          <tr>
            <th className="px-2 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Level</th>
            <th className="px-2 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">System</th>
            <th className="px-2 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Group</th>
            <th className="px-2 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Equipment</th>
            <th className="px-2 py-3 text-right font-semibold text-gray-700 whitespace-nowrap">kW</th>
            <th className="px-2 py-3 text-right font-semibold text-gray-700 whitespace-nowrap">Qty</th>
            <th className="px-2 py-3 text-right font-semibold text-gray-700 whitespace-nowrap">Load%</th>
            <th className="px-2 py-3 text-right font-semibold text-gray-700 whitespace-nowrap">Hrs/Day</th>
            <th className="px-2 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Control</th>
            <th className="px-2 py-3 text-right font-semibold text-gray-700 bg-yellow-100 whitespace-nowrap">Est kWh/yr</th>
            <th className="px-2 py-3 text-right font-semibold text-gray-700 bg-yellow-100 whitespace-nowrap">Share%</th>
            <th className="px-2 py-3 text-center font-semibold text-gray-700 bg-yellow-100 whitespace-nowrap">Conf</th>
            <th className="px-2 py-3 text-center font-semibold text-gray-700 bg-yellow-100 whitespace-nowrap">SEU</th>
            <th className="px-2 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">Remarks</th>
            <th className="px-2 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {dataWithShare.map((row, idx) => (
            <tr key={row.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-2 py-2">
                <select value={row.seu_level || ''} onChange={(e) => updateRow(idx, 'seu_level', e.target.value)} className="w-full px-2 py-1 border rounded text-sm">
                  {SEU_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </td>
              <td className="px-2 py-2">
                <select value={row.system_name || ''} onChange={(e) => updateRow(idx, 'system_name', e.target.value)} className="w-full px-2 py-1 border rounded text-sm">
                  <option value="">Select...</option>
                  {EQUIPMENT_TYPES.electricity.systems.map(s => <option key={s} value={s}>{s}</option>)}
                  {/* Preserve imported values that aren't in the predefined list (e.g. custom taxonomy from CSV) */}
                  {row.system_name && !EQUIPMENT_TYPES.electricity.systems.includes(row.system_name) && (
                    <option key={`__imported_${row.system_name}`} value={row.system_name}>{row.system_name} (imported)</option>
                  )}
                </select>
              </td>
              <td className="px-2 py-2">
                <select value={row.group_name || ''} onChange={(e) => updateRow(idx, 'group_name', e.target.value)} className="w-full px-2 py-1 border rounded text-sm">
                  <option value="">Select...</option>
                  {EQUIPMENT_TYPES.electricity.groups.map(g => <option key={g} value={g}>{g}</option>)}
                  {row.group_name && !EQUIPMENT_TYPES.electricity.groups.includes(row.group_name) && (
                    <option key={`__imported_${row.group_name}`} value={row.group_name}>{row.group_name} (imported)</option>
                  )}
                </select>
              </td>
              <td className="px-2 py-2">
                {/* title: free text has no length limit, so the widest sensible
                    column can still be out-run — hovering shows the full value. */}
                <input type="text" value={row.equipment_type || ''} onChange={(e) => updateRow(idx, 'equipment_type', e.target.value)} placeholder="Name/Tag" title={row.equipment_type || ''} className="w-full px-2 py-1 border rounded text-sm" />
              </td>
              <td className="px-2 py-2">
                <input type="number" value={row.rated_kw || ''} onChange={(e) => updateRow(idx, 'rated_kw', e.target.value)} className="w-20 px-2 py-1 border rounded text-sm text-right" />
              </td>
              <td className="px-2 py-2">
                <input type="number" value={row.quantity || ''} onChange={(e) => updateRow(idx, 'quantity', e.target.value)} className="w-14 px-2 py-1 border rounded text-sm text-right" />
              </td>
              <td className="px-2 py-2">
                <input type="number" value={row.load_percent || ''} onChange={(e) => updateRow(idx, 'load_percent', e.target.value)} className="w-16 px-2 py-1 border rounded text-sm text-right" />
              </td>
              <td className="px-2 py-2">
                <input type="number" value={row.hours_per_day || ''} onChange={(e) => updateRow(idx, 'hours_per_day', e.target.value)} className="w-16 px-2 py-1 border rounded text-sm text-right" />
              </td>
              <td className="px-2 py-2">
                <select value={row.control_type || 'On/Off'} onChange={(e) => updateRow(idx, 'control_type', e.target.value)} className="w-full px-2 py-1 border rounded text-sm">
                  {CONTROL_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </td>
              <td className="px-2 py-2 bg-yellow-50 text-right font-medium">
                <input type="number" value={row.annual_kwh || ''} onChange={(e) => updateRow(idx, 'annual_kwh', parseFloat(e.target.value) || null)} className="w-24 px-2 py-1 border rounded text-sm text-right bg-yellow-50" />
              </td>
              <td className="px-2 py-2 bg-yellow-50 text-right font-medium text-blue-600">
                {row.share_percent?.toFixed(1) || '0.0'}%
              </td>
              <td className="px-2 py-2 bg-yellow-50 text-center">
                <select value={row.confidence || 'Low'} onChange={(e) => updateRow(idx, 'confidence', e.target.value)} className="px-2 py-1 border rounded text-xs">
                  <option value="Low">🟡 Low</option>
                  <option value="Medium">🔵 Medium</option>
                </select>
              </td>
              <td className="px-2 py-2 bg-yellow-50 text-center">
                <input type="checkbox" checked={row.is_seu || row.auto_seu} onChange={(e) => updateRow(idx, 'is_seu', e.target.checked)} className="w-4 h-4" />
              </td>
              <td className="px-2 py-2">
                <input type="text" value={row.remarks || ''} onChange={(e) => updateRow(idx, 'remarks', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" />
              </td>
              <td className="px-2 py-2 text-center">
                <button onClick={() => removeRow(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                  <Trash2 className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-yellow-100 font-semibold">
          <tr>
            <td colSpan={9} className="px-2 py-2 text-right">Total:</td>
            <td className="px-2 py-2 text-right">{totalKwh.toLocaleString()}</td>
            <td className="px-2 py-2 text-right">100%</td>
            <td colSpan={4}></td>
          </tr>
        </tfoot>
      </table>
      <button onClick={addRow} className="m-4 flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200">
        <Plus className="w-4 h-4" /> Add Row
      </button>
    </div>
  );
};

// ==================== FUEL TABLE ====================
const FuelTable = ({ data, setData, createEmptyRow }) => {
  const updateRow = (index, field, value) => {
    const newData = [...data];
    newData[index] = { ...newData[index], [field]: value };
    newData[index].annual_gj = calculateFuelEnergy(newData[index]);
    newData[index].confidence = determineConfidence(newData[index], 'fuel');
    setData(newData);
  };
  
  const addRow = () => setData([...data, createEmptyRow()]);
  const removeRow = (index) => setData(data.filter((_, i) => i !== index));
  
  const totalGj = data.reduce((sum, row) => sum + (row.annual_gj || 0), 0);
  const dataWithShare = data.map(row => ({
    ...row,
    share_percent: totalGj > 0 ? ((row.annual_gj || 0) / totalGj * 100) : 0,
    auto_seu: totalGj > 0 ? ((row.annual_gj || 0) / totalGj * 100) >= 5 : false
  }));
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1400px] text-sm">
        <thead className="bg-orange-50">
          <tr>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Equipment</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Location</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Fuel Type</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">Monthly Qty</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Unit</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700 bg-orange-100">Annual GJ</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700 bg-orange-100">Share%</th>
            <th className="px-3 py-3 text-center font-semibold text-gray-700 bg-orange-100">Conf</th>
            <th className="px-3 py-3 text-center font-semibold text-gray-700 bg-orange-100">SEU</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Remarks</th>
            <th className="px-3 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {dataWithShare.map((row, idx) => (
            <tr key={row.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-3 py-2">
                <select value={row.equipment_type || ''} onChange={(e) => updateRow(idx, 'equipment_type', e.target.value)} className="w-full px-2 py-1 border rounded text-sm">
                  <option value="">Select...</option>
                  {EQUIPMENT_TYPES.fuel.map(e => <option key={e} value={e}>{e}</option>)}
                  {row.equipment_type && !EQUIPMENT_TYPES.fuel.includes(row.equipment_type) && (
                    <option key={`__imported_${row.equipment_type}`} value={row.equipment_type}>{row.equipment_type} (imported)</option>
                  )}
                </select>
              </td>
              <td className="px-3 py-2">
                <input type="text" value={row.process_area || ''} onChange={(e) => updateRow(idx, 'process_area', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" />
              </td>
              <td className="px-3 py-2">
                <select value={row.fuel_type || 'PNG'} onChange={(e) => updateRow(idx, 'fuel_type', e.target.value)} className="w-full px-2 py-1 border rounded text-sm">
                  {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </td>
              <td className="px-3 py-2">
                <input type="number" value={row.monthly_fuel_qty || ''} onChange={(e) => updateRow(idx, 'monthly_fuel_qty', e.target.value)} className="w-24 px-2 py-1 border rounded text-sm text-right" />
              </td>
              <td className="px-3 py-2">
                <select value={row.fuel_unit || 'SCM'} onChange={(e) => updateRow(idx, 'fuel_unit', e.target.value)} className="w-20 px-2 py-1 border rounded text-sm">
                  <option value="SCM">SCM</option>
                  <option value="L">L</option>
                  <option value="kg">kg</option>
                  <option value="MT">MT</option>
                </select>
              </td>
              <td className="px-3 py-2 bg-orange-50 text-right font-medium">
                <input type="number" value={row.annual_gj || ''} onChange={(e) => updateRow(idx, 'annual_gj', parseFloat(e.target.value) || null)} className="w-20 px-2 py-1 border rounded text-sm text-right bg-orange-50" />
              </td>
              <td className="px-3 py-2 bg-orange-50 text-right font-medium text-blue-600">{row.share_percent?.toFixed(1) || '0.0'}%</td>
              <td className="px-3 py-2 bg-orange-50 text-center">
                <select value={row.confidence || 'Low'} onChange={(e) => updateRow(idx, 'confidence', e.target.value)} className="px-2 py-1 border rounded text-xs">
                  <option value="Low">🟡 Low</option>
                  <option value="Medium">🔵 Medium</option>
                </select>
              </td>
              <td className="px-3 py-2 bg-orange-50 text-center">
                <input type="checkbox" checked={row.is_seu || row.auto_seu} onChange={(e) => updateRow(idx, 'is_seu', e.target.checked)} className="w-4 h-4" />
              </td>
              <td className="px-3 py-2">
                <input type="text" value={row.remarks || ''} onChange={(e) => updateRow(idx, 'remarks', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" />
              </td>
              <td className="px-3 py-2 text-center">
                <button onClick={() => removeRow(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-orange-100 font-semibold">
          <tr>
            <td colSpan={5} className="px-3 py-2 text-right">Total:</td>
            <td className="px-3 py-2 text-right">{totalGj.toFixed(1)}</td>
            <td className="px-3 py-2 text-right">100%</td>
            <td colSpan={4}></td>
          </tr>
        </tfoot>
      </table>
      <button onClick={addRow} className="m-4 flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200">
        <Plus className="w-4 h-4" /> Add Row
      </button>
    </div>
  );
};

// ==================== WATER TABLE ====================
const WaterTable = ({ data, setData, createEmptyRow }) => {
  const updateRow = (index, field, value) => {
    const newData = [...data];
    newData[index] = { ...newData[index], [field]: value };
    newData[index].annual_kl = calculateWaterAnnual(newData[index]);
    newData[index].confidence = determineConfidence(newData[index], 'water');
    setData(newData);
  };
  
  const addRow = () => setData([...data, createEmptyRow()]);
  const removeRow = (index) => setData(data.filter((_, i) => i !== index));
  
  const totalKl = data.reduce((sum, row) => sum + (row.annual_kl || 0), 0);
  const dataWithShare = data.map(row => ({
    ...row,
    share_percent: totalKl > 0 ? ((row.annual_kl || 0) / totalKl * 100) : 0,
    auto_seu: totalKl > 0 ? ((row.annual_kl || 0) / totalKl * 100) >= 5 : false
  }));
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1400px] text-sm">
        <thead className="bg-blue-50">
          <tr>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Equipment/Process</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Location</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Source</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">Daily KL</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700 bg-blue-100">Annual KL</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700 bg-blue-100">Share%</th>
            <th className="px-3 py-3 text-center font-semibold text-gray-700">Recycling</th>
            <th className="px-3 py-3 text-center font-semibold text-gray-700 bg-blue-100">Conf</th>
            <th className="px-3 py-3 text-center font-semibold text-gray-700 bg-blue-100">SEU</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Remarks</th>
            <th className="px-3 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {dataWithShare.map((row, idx) => (
            <tr key={row.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-3 py-2">
                <select value={row.equipment_type || ''} onChange={(e) => updateRow(idx, 'equipment_type', e.target.value)} className="w-full px-2 py-1 border rounded text-sm">
                  <option value="">Select...</option>
                  {EQUIPMENT_TYPES.water.map(e => <option key={e} value={e}>{e}</option>)}
                  {row.equipment_type && !EQUIPMENT_TYPES.water.includes(row.equipment_type) && (
                    <option key={`__imported_${row.equipment_type}`} value={row.equipment_type}>{row.equipment_type} (imported)</option>
                  )}
                </select>
              </td>
              <td className="px-3 py-2">
                <input type="text" value={row.process_area || ''} onChange={(e) => updateRow(idx, 'process_area', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" />
              </td>
              <td className="px-3 py-2">
                <select value={row.source || 'Municipal'} onChange={(e) => updateRow(idx, 'source', e.target.value)} className="w-full px-2 py-1 border rounded text-sm">
                  {WATER_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
              <td className="px-3 py-2">
                <input type="number" value={row.daily_kl || ''} onChange={(e) => updateRow(idx, 'daily_kl', e.target.value)} className="w-20 px-2 py-1 border rounded text-sm text-right" />
              </td>
              <td className="px-3 py-2 bg-blue-50 text-right font-medium">
                <input type="number" value={row.annual_kl || ''} onChange={(e) => updateRow(idx, 'annual_kl', parseFloat(e.target.value) || null)} className="w-24 px-2 py-1 border rounded text-sm text-right bg-blue-50" />
              </td>
              <td className="px-3 py-2 bg-blue-50 text-right font-medium text-blue-600">{row.share_percent?.toFixed(1) || '0.0'}%</td>
              <td className="px-3 py-2 text-center">
                <input type="checkbox" checked={row.recycling || false} onChange={(e) => updateRow(idx, 'recycling', e.target.checked)} className="w-4 h-4" />
              </td>
              <td className="px-3 py-2 bg-blue-50 text-center">
                <select value={row.confidence || 'Low'} onChange={(e) => updateRow(idx, 'confidence', e.target.value)} className="px-2 py-1 border rounded text-xs">
                  <option value="Low">🟡 Low</option>
                  <option value="Medium">🔵 Medium</option>
                </select>
              </td>
              <td className="px-3 py-2 bg-blue-50 text-center">
                <input type="checkbox" checked={row.is_seu || row.auto_seu} onChange={(e) => updateRow(idx, 'is_seu', e.target.checked)} className="w-4 h-4" />
              </td>
              <td className="px-3 py-2">
                <input type="text" value={row.remarks || ''} onChange={(e) => updateRow(idx, 'remarks', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" />
              </td>
              <td className="px-3 py-2 text-center">
                <button onClick={() => removeRow(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-blue-100 font-semibold">
          <tr>
            <td colSpan={4} className="px-3 py-2 text-right">Total:</td>
            <td className="px-3 py-2 text-right">{totalKl.toLocaleString()}</td>
            <td className="px-3 py-2 text-right">100%</td>
            <td colSpan={5}></td>
          </tr>
        </tfoot>
      </table>
      <button onClick={addRow} className="m-4 flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">
        <Plus className="w-4 h-4" /> Add Row
      </button>
    </div>
  );
};

// ==================== WASTE TABLE ====================
const WasteTable = ({ data, setData, createEmptyRow }) => {
  const updateRow = (index, field, value) => {
    const newData = [...data];
    newData[index] = { ...newData[index], [field]: value };
    newData[index].annual_qty = calculateWasteAnnual(newData[index]);
    newData[index].confidence = determineConfidence(newData[index], 'waste');
    setData(newData);
  };
  
  const addRow = () => setData([...data, createEmptyRow()]);
  const removeRow = (index) => setData(data.filter((_, i) => i !== index));
  
  const totalQty = data.reduce((sum, row) => sum + (row.annual_qty || 0), 0);
  const dataWithShare = data.map(row => ({
    ...row,
    share_percent: totalQty > 0 ? ((row.annual_qty || 0) / totalQty * 100) : 0,
    auto_seu: totalQty > 0 ? ((row.annual_qty || 0) / totalQty * 100) >= 5 : false
  }));
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1650px] text-sm">
        <thead className="bg-green-50">
          <tr>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Waste Stream</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Location</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Type</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Category</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">Monthly Qty</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Unit</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700 bg-green-100">Annual</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700 bg-green-100">Share%</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Disposal</th>
            <th className="px-3 py-3 text-center font-semibold text-gray-700 bg-green-100">Conf</th>
            <th className="px-3 py-3 text-center font-semibold text-gray-700 bg-green-100">SEU</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Remarks</th>
            <th className="px-3 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {dataWithShare.map((row, idx) => (
            <tr key={row.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-3 py-2">
                <select value={row.equipment_type || ''} onChange={(e) => updateRow(idx, 'equipment_type', e.target.value)} className="w-full px-2 py-1 border rounded text-sm">
                  <option value="">Select...</option>
                  {EQUIPMENT_TYPES.waste.map(e => <option key={e} value={e}>{e}</option>)}
                  {row.equipment_type && !EQUIPMENT_TYPES.waste.includes(row.equipment_type) && (
                    <option key={`__imported_${row.equipment_type}`} value={row.equipment_type}>{row.equipment_type} (imported)</option>
                  )}
                </select>
              </td>
              <td className="px-3 py-2">
                <input type="text" value={row.process_area || ''} onChange={(e) => updateRow(idx, 'process_area', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" />
              </td>
              <td className="px-3 py-2">
                <select value={row.waste_type || 'Non-Hazardous'} onChange={(e) => updateRow(idx, 'waste_type', e.target.value)} className="w-full px-2 py-1 border rounded text-sm">
                  {WASTE_CATEGORIES.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </td>
              <td className="px-3 py-2">
                <input type="text" value={row.category || ''} onChange={(e) => updateRow(idx, 'category', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" placeholder="Cat code" />
              </td>
              <td className="px-3 py-2">
                <input type="number" value={row.monthly_qty || ''} onChange={(e) => updateRow(idx, 'monthly_qty', e.target.value)} className="w-20 px-2 py-1 border rounded text-sm text-right" />
              </td>
              <td className="px-3 py-2">
                <select value={row.unit || 'MT'} onChange={(e) => updateRow(idx, 'unit', e.target.value)} className="w-16 px-2 py-1 border rounded text-sm">
                  <option value="MT">MT</option>
                  <option value="kg">kg</option>
                  <option value="KL">KL</option>
                  <option value="Nos">Nos</option>
                </select>
              </td>
              <td className="px-3 py-2 bg-green-50 text-right font-medium">
                <input type="number" value={row.annual_qty || ''} onChange={(e) => updateRow(idx, 'annual_qty', parseFloat(e.target.value) || null)} className="w-20 px-2 py-1 border rounded text-sm text-right bg-green-50" />
              </td>
              <td className="px-3 py-2 bg-green-50 text-right font-medium text-blue-600">{row.share_percent?.toFixed(1) || '0.0'}%</td>
              <td className="px-3 py-2">
                <select value={row.disposal_method || ''} onChange={(e) => updateRow(idx, 'disposal_method', e.target.value)} className="w-full px-2 py-1 border rounded text-sm">
                  <option value="">Select...</option>
                  {DISPOSAL_METHODS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </td>
              <td className="px-3 py-2 bg-green-50 text-center">
                <select value={row.confidence || 'Low'} onChange={(e) => updateRow(idx, 'confidence', e.target.value)} className="px-2 py-1 border rounded text-xs">
                  <option value="Low">🟡 Low</option>
                  <option value="Medium">🔵 Medium</option>
                </select>
              </td>
              <td className="px-3 py-2 bg-green-50 text-center">
                <input type="checkbox" checked={row.is_seu || row.auto_seu} onChange={(e) => updateRow(idx, 'is_seu', e.target.checked)} className="w-4 h-4" />
              </td>
              <td className="px-3 py-2">
                <input type="text" value={row.remarks || ''} onChange={(e) => updateRow(idx, 'remarks', e.target.value)} className="w-full px-2 py-1 border rounded text-sm" />
              </td>
              <td className="px-3 py-2 text-center">
                <button onClick={() => removeRow(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-green-100 font-semibold">
          <tr>
            <td colSpan={6} className="px-3 py-2 text-right">Total:</td>
            <td className="px-3 py-2 text-right">{totalQty.toFixed(1)}</td>
            <td className="px-3 py-2 text-right">100%</td>
            <td colSpan={5}></td>
          </tr>
        </tfoot>
      </table>
      <button onClick={addRow} className="m-4 flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200">
        <Plus className="w-4 h-4" /> Add Row
      </button>
    </div>
  );
};

export default SEUModule;
